import { Icon } from "@mui/material";
import {
  AddBox as AddBoxIcon,
  Autorenew as AutorenewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FormatColorFill,
  Memory as MemoryIcon,
  PlaylistAdd as PlaylistAddIcon,
} from "@mui/icons-material";
import { isNil } from "lodash-es";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MenuOptionType } from "@/controls/contextMenu/models/MenuOptionType";
import { HeatDataEntryType } from "../models/HeatDataEntryType";

const icons = {
  eaf: () => <FormatColorFill />,
  lmf: () => (
    <Icon style={{ fontSize: "1.5em", marginLeft: "-3px" }}>thermostat</Icon>
  ),
  caster: () => <MemoryIcon />,
  lab: () => (
    <Icon style={{ fontSize: "1.5em", marginLeft: "-3px" }}>science</Icon>
  ),
  delete: () => <DeleteIcon />,
  edit: () => <EditIcon />,
  new: () => <PlaylistAddIcon />,
  pourback: () => <AutorenewIcon />,
  undelete: () => <AddBoxIcon />,
};

type Props = {
  setHeatIdForEdit: (id: number) => void;
  setModalEditHeat: (value: boolean) => void;
  setModalCreateHeat: (value: boolean) => void;
  setModalPourback: (heat: HeatDataEntryType) => void;
  onUndeleteHeat: (heatId: number) => void;
};

export const useHeatsOptionsFunctions = ({
  setHeatIdForEdit,
  setModalEditHeat,
  setModalCreateHeat,
  setModalPourback,
  onUndeleteHeat,
}: Props) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageDeleteModal, setMessageDeleteModal] = useState("");
  const [heatIdForDelete, setHeatIdForDelete] = useState<number | null>(null);
  const navigate = useNavigate();

  const goToEAFDataEntry = (EAFProcessId: number | null) => {
    if (!isNil(EAFProcessId)) navigate(`../eaf-data-entry/${EAFProcessId}`);
  };

  const goToLMFDataEntry = (EAFProcessId: number | null) => {
    if (!isNil(EAFProcessId)) navigate(`../lmf-data-entry/${EAFProcessId}`);
  };

  const goToCasterDataEntry = (CASProcessId: number | null) => {
    if (!isNil(CASProcessId)) navigate(`../caster-data-entry/${CASProcessId}`);
  };

  const goToLABDataEntry = (EAFProcessId: number | null) => {
    if (!isNil(EAFProcessId)) navigate(`../lab-data-entry/${EAFProcessId}`);
  };

  const getMenuOptions = (data: HeatDataEntryType | null): MenuOptionType[] => {
    let options: MenuOptionType[] = [];

    // Deleted heats: only allow undelete
    if (data && !data.IsActive) {
      return [
        {
          name: "Undelete Heat",
          key: "undelete_heat",
          onClick: () => onUndeleteHeat(data.HeatId),
          icon: icons.undelete(),
        },
      ];
    }

    if (data?.EAFProcessId) {
      options.push({
        name: "See in EAF",
        key: "go_to_eaf",
        onClick: () => goToEAFDataEntry(data.EAFProcessId),
        icon: icons.eaf(),
      });
    }

    if (data?.LMFProcessId) {
      options.push({
        name: "See in LMF",
        key: "go_to_lmf",
        onClick: () => goToLMFDataEntry(data.LMFProcessId),
        icon: icons.lmf(),
      });
    }

    if (data?.CASProcessId) {
      options.push({
        name: "See in Caster",
        key: "go_to_caster_data_entry",
        style: { fontWeight: "bold" },
        onClick: () => goToCasterDataEntry(data.CASProcessId),
        icon: icons.caster(),
      });
    }

    if (data?.EAFProcessId) {
      options.push({
        name: "See in LAB",
        key: "go_to_LAB",
        onClick: () => goToLABDataEntry(data.EAFProcessId),
        icon: icons.lab(),
      });
    }

    if (data?.HeatId) {
      options = options.concat([
        {
          name: "Delete Heat",
          key: "delete_heat",
          onClick: () => {
            setHeatIdForDelete(data.HeatId);
            setMessageDeleteModal(
              `The heat ${data.HeatName} will be deleted. Do you want to continue?`,
            );
            setShowDeleteModal(true);
          },
          icon: icons.delete(),
        },
        {
          name: "Edit Heat",
          key: "edit_heat",
          onClick: () => {
            setHeatIdForEdit(data.HeatId);
            setModalEditHeat(true);
          },
          icon: icons.edit(),
        },
        {
          name: "New Heat",
          key: "new_heat",
          onClick: () => {
            setModalCreateHeat(true);
          },
          icon: icons.new(),
        },
      ]);
      if (data.HeatStatusCode !== "S" && data.HeatStatusCode !== "M") {
        options.push({
          name: "Pourback",
          key: "declare_pourback",
          onClick: () => {
            setModalPourback(data);
          },
          icon: icons.pourback(),
        });
      }
    }

    return options;
  };

  return {
    getMenuOptions,
    goToCasterDataEntry,
    goToEAFDataEntry,
    goToLMFDataEntry,
    goToLABDataEntry,
    showDeleteModal,
    messageDeleteModal,
    heatIdForDelete,
    setShowDeleteModal,
    setMessageDeleteModal,
    setHeatIdForDelete,
  };
};
